import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import { eq, user } from "@fieldcheck/powersync";
import { useUserPreferences } from "@/providers/user-preferences-context";
import {
  type ColorMode,
  type DesignTokens,
  getUnionThemeQuery,
  queryKeys,
  resolveTheme,
  THEME_EXTRA_VARS_MAP,
  type ThemeRow,
  type ThemeTokens,
  TOKEN_TO_CSS_MAP,
  updateBlurMutation,
  updateFollowDeviceThemeMutation,
  updateGlobalThemeMutation,
  VISITOR_ID,
} from "@fieldcheck/shared";

type BackgroundStyle = "gradients" | "lines";

/**
 * Modo de color canónico, disponible para TODOS (con y sin login). Es ORTOGONAL
 * al tema activo (por defecto o personalizado): el modo solo elige la VARIANTE
 * clara/oscura del tema, sin cambiarlo.
 *  - "system": la variante sigue al SO (prefers-color-scheme).
 *  - "light"/"dark": override EXPLÍCITO que GANA sobre el SO.
 * Se persiste en localStorage (por dispositivo) y lo lee también el script
 * pre-paint de index.html para evitar el flash al recargar.
 *
 * Reexportado desde @fieldcheck/shared, donde vive la lógica pura `resolveTheme`.
 */
export type { ColorMode };

/** Clave de localStorage del modo de color (prefijo por app). */
export const COLOR_MODE_STORAGE_KEY = "fieldcheck-color-mode";

interface ThemeContextValue {
  tokens: () => ThemeTokens | null;
  isDark: () => boolean;
  isBlur: () => boolean;
  followDevice: () => boolean;
  colorMode: () => ColorMode;
  /**
   * Fija el modo canónico (localStorage) SIN tocar el tema activo. El modo y el
   * tema son ejes ORTOGONALES: cambiar de modo conserva el tema personalizado.
   */
  selectColorMode: (mode: ColorMode) => void;
  /** Fija solo el modo (localStorage) sin tocar el tema custom. Alias de selectColorMode. */
  setColorMode: (mode: ColorMode) => void;
  /** Vuelve al TEMA POR DEFECTO (limpia el tema personalizado global) sin tocar el modo. */
  resetToDefaultTheme: () => void;
  toggleBlur: () => Promise<void>;
  toggleFollowDevice: () => Promise<void>;
  allThemes: () => ThemeRow[] | undefined;
  systemIsDark: () => boolean;
  isThemeLoading: () => boolean;
  backgroundStyle: () => BackgroundStyle;
  setBackgroundStyle: (style: BackgroundStyle) => void;
  toggleBackgroundStyle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>();

function serializeCSSValue(value: unknown): string {
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  // oxlint-disable-next-line typescript/no-base-to-string
  return String(value);
}

function applyCSSVars(
  root: HTMLElement,
  source: ThemeTokens | DesignTokens,
  isDark: boolean,
  isBlur: boolean,
) {
  for (const [tokenKey, cssVar] of Object.entries(TOKEN_TO_CSS_MAP)) {
    // Runtime property access — avoids index-signature casts.
    // getOwnPropertyDescriptor returns the raw value typed as unknown.
    const value: unknown = Object.getOwnPropertyDescriptor(source, tokenKey)?.value;
    if (value === undefined || value === null) {
      continue;
    }
    const serialized = serializeCSSValue(value);
    root.style.setProperty(cssVar, serialized);

    // Expand to multi-mapped variables
    const extraVars = (THEME_EXTRA_VARS_MAP as Record<string, string[] | undefined>)[tokenKey];
    if (extraVars) {
      for (const ev of extraVars) {
        root.style.setProperty(ev, serialized);
      }
    }
  }
  root.style.setProperty("--is-dark", isDark ? "1" : "0");
  root.style.setProperty("--is-blur", isBlur ? "1" : "0");
}

export const ThemeProvider: ParentComponent = (props) => {
  const { db: psDb } = usePowerSyncContext();
  // Use session.userId (reactive function) instead of session.data?.user.id.
  // session.data is evaluated once at call-time (not a reactive getter), so on
  // page refresh — when the session machine is still priming from localStorage —
  // session.data is undefined and userId() would forever return undefined,
  // keeping theme queries disabled. session.userId() reacts correctly to the
  // machine finishing its priming phase.
  const { userId } = useSession();
  const drizzle = useDrizzle();
  const { preferences, isLoading: isPreferencesLoading } = useUserPreferences();

  // Ensure the current user has a settings record in the user table.
  // In visitor/guest mode, there is no server synchronization, so no user rows exist.
  // We must seed/insert a default row for the current userId to enable theme queries and mutations.
  //
  // Guard: only VISITOR_ID reaches the seed below. Real accounts already get
  // their `user` row created server-side, unconditionally, on every signup/OAuth
  // path (packages/core-rust/src/db/user.rs:101,350 — `INSERT INTO "user"`).
  // Seeding locally for a real uid raced that server-created row during the
  // checkpoint window: two inserts for the same id, one of them a 3-column
  // ghost — this was the root cause of the `user`-table PowerSync checkpoint
  // rebuild races that `patchRowVerified` has to defend against elsewhere. See
  // the docblock in `packages/shared/src/users/mutations.ts` for why that
  // protection stays regardless. Do NOT widen this back to "any uid" — see
  // `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`,
  // which proves removing the seed for real accounts causes zero visual
  // regression (both branches collapse to the same `themeRow: null`) while it
  // closes the race. Same pattern as i18n-provider.tsx and
  // typography-provider.tsx (`userId !== VISITOR_ID` / `userId === VISITOR_ID`).
  createEffect(() => {
    const db = drizzle();
    const uid = userId();
    if (!db || uid !== VISITOR_ID) {
      return;
    }
    void (async () => {
      try {
        const existing = await db.select().from(user).where(eq(user.id, uid)).limit(1);
        if (existing.length === 0) {
          try {
            await db.insert(user).values({
              id: uid,
              is_blur: 1,
              follow_device_theme: 1,
            });
            console.info(`[ThemeProvider] Seeded default user settings row for ${uid}`);
          } catch (insertErr) {
            const errMsg = insertErr instanceof Error ? insertErr.message : String(insertErr);
            // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): mensaje de BD, no UI
            if (errMsg.includes("UNIQUE constraint failed")) {
              console.info(
                `[ThemeProvider] User settings row for ${uid} was created concurrently by sync`,
              );
              return;
            }
            throw insertErr;
          }
        }
      } catch (err) {
        console.error("[ThemeProvider] Failed to ensure user settings row:", err);
      }
    })();
  });

  // Detect system dark mode and react to OS changes
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): media query CSS, no UI
  const mq = globalThis.matchMedia("(prefers-color-scheme: dark)");
  const [systemIsDark, setSystemIsDark] = createSignal(mq.matches);
  const handleMqChange = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
  mq.addEventListener("change", handleMqChange);
  onCleanup(() => {
    mq.removeEventListener("change", handleMqChange);
  });

  // Query for the user's active theme (global + all list themes via UNION ALL).
  // We do NOT include currentGlobalThemeId() in the queryKey even though it is
  // available from userSettingsQuery. Adding it caused a double-invalidation race:
  //   1. onChangeWithCallback fires for the `user` table → invalidates current key
  //      → refetch → correct theme ✓
  //   2. userSettingsQuery refetches → currentGlobalThemeId() updates → queryKey
  //      changes → TanStack creates an empty new cache entry → data = undefined
  //      → tokens() = null → CSS vars freeze at the previous theme (one behind ✗)
  // getUnionThemeQuery already JOINs `user`, so resolveTables monitors it and
  // onChangeWithCallback fires reliably whenever global_theme_id changes.
  const themeQuery = createPowerSyncQuery<ThemeRow>({
    queryKey: () => queryKeys.themes.active(userId()),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      const uid = userId();
      if (!uid || !db) {
        return null;
      }
      return getUnionThemeQuery(db, uid);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
  });

  const [optimisticBlur, setOptimisticBlur] = createSignal<boolean | null>(null);
  const [optimisticFollowDevice, setOptimisticFollowDevice] = createSignal<boolean | null>(null);
  const savedBgStyle = localStorage.getItem("fieldcheck-bg-style");
  const [backgroundStyleState, setBackgroundStyleState] = createSignal<BackgroundStyle>(
    savedBgStyle === "gradients" || savedBgStyle === "lines" ? savedBgStyle : "gradients",
  );

  // Modo de color canónico (system/light/dark), por dispositivo. Disponible sin login.
  const savedColorMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  const [colorMode, setColorModeState] = createSignal<ColorMode>(
    savedColorMode === "light" || savedColorMode === "dark" ? savedColorMode : "system",
  );

  const isBlur = createMemo(() => optimisticBlur() ?? preferences()?.is_blur !== 0);

  const followDevice = createMemo(
    () => optimisticFollowDevice() ?? Boolean(preferences()?.follow_device_theme),
  );

  // Retain the last non-empty theme rows. A PowerSync invalidation burst (the
  // EXPLAIN/resolveTables waterfall seen when any table changes) can momentarily
  // leave `themeQuery.data` empty/undefined mid-refetch. Downstream consumers —
  // AppBackground's glow colors (`getGlowColors`), `useAppThemeState`, and the
  // ListThemeProvider — would then fall back to hardcoded per-route glows and
  // flash. Holding the previous rows keeps the resolved theme stable until real
  // data lands. A genuine theme change still carries rows, so it passes through.
  let lastThemes: ThemeRow[] | undefined = undefined;
  const allThemes = createMemo<ThemeRow[] | undefined>(() => {
    const { data } = themeQuery;
    if (data && data.length > 0) {
      lastThemes = data;
      return data;
    }
    return lastThemes ?? data;
  });

  // Resolución CANÓNICA del tema (lógica pura, unit-testada en @fieldcheck/shared).
  // Dos ejes ortogonales: (a) QUÉ tema (global custom o por defecto) y (b) QUÉ
  // modo (system/light/dark). El modo explícito GANA sobre el SO; el tema se
  // CONSERVA al cambiar de modo. `resolveTheme` elige la variante clara/oscura
  // del tema (usando su par claro/oscuro si existe).
  const resolved = createMemo(() => {
    const globalRow = allThemes()?.find((r) => r.target_id === "global") ?? null;
    // Una fila global sin id (leftJoin sin tema seleccionado) = tema por defecto.
    const themeRow: ThemeRow | null = globalRow?.id ? globalRow : null;
    return resolveTheme({
      themeRow,
      mode: colorMode(),
      systemPrefersDark: systemIsDark(),
      isBlurContext: isBlur(),
      parentTokens: null,
    });
  });

  const tokens = createMemo<ThemeTokens | null>(() => resolved().tokens);
  const isDark = createMemo(() => resolved().effectiveMode === "dark");

  // Apply CSS variables and dark/light class to <html>
  createEffect(() => {
    const r = resolved();
    const dark = r.effectiveMode === "dark";
    const blur = isBlur();
    const mode = colorMode();
    const root = document.documentElement;

    const applyTheme = () => {
      // El modo se refleja como clase (.light/.dark) Y como atributo data-mode en
      // <html>. La clase explícita GANA sobre cualquier @media (prefers-color-scheme)
      // — por eso el modo explícito supersede al SO. `color-scheme` alinea los
      // controles nativos (scrollbars, inputs) con la variante efectiva.
      root.style.setProperty("color-scheme", dark ? "dark" : "light");
      root.classList.toggle("dark", dark);
      root.classList.toggle("light", !dark);
      root.dataset.mode = mode;
      // `resolveTheme` ya entrega los tokens correctos (base para el tema por
      // defecto, o la variante clara/oscura del tema personalizado activo).
      applyCSSVars(root, r.tokens, dark, blur);
    };

    if ("startViewTransition" in document && typeof document.startViewTransition === "function") {
      const transition = document.startViewTransition(applyTheme);
      // Catch skipped/aborted transitions to prevent uncaught promise rejection warnings
      void (async () => {
        try {
          await transition.ready;
        } catch {
          // ignore skipped transition error
        }
        try {
          await transition.finished;
        } catch {
          // ignore skipped transition error
        }
      })();
    } else {
      applyTheme();
    }
    // `allThemes` retiene las últimas filas no vacías, así que durante un refetch
    // de PowerSync `resolved()` sigue entregando el tema custom (no parpadea a base).
  });

  const toggleBlur = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    const newValue = !isBlur();
    setOptimisticBlur(newValue);
    try {
      await updateBlurMutation(db, uid, newValue);
    } catch (err) {
      setOptimisticBlur(null);
      console.error("Error toggling blur:", err);
    }
  };

  const toggleFollowDevice = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    const newValue = !followDevice();
    setOptimisticFollowDevice(newValue);
    try {
      await updateFollowDeviceThemeMutation(db, uid, newValue);
    } catch (err) {
      setOptimisticFollowDevice(null);
      console.error("Error toggling follow device:", err);
    }
  };

  const setColorMode = (mode: ColorMode) => {
    setColorModeState(mode);
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  };

  // Modo y tema son ORTOGONALES: elegir un modo canónico NO toca el tema activo.
  // El tema personalizado se conserva; el modo solo elige su variante clara/oscura.
  // (Para volver al tema por defecto está `resetToDefaultTheme`.)
  const selectColorMode = (mode: ColorMode) => {
    setColorMode(mode);
  };

  // Vuelve al TEMA POR DEFECTO limpiando el tema personalizado global, sin tocar
  // el modo (el eje claro/oscuro elegido se mantiene).
  const resetToDefaultTheme = () => {
    const db = drizzle();
    const uid = userId();
    if (db && uid) {
      void updateGlobalThemeMutation(db, uid, null).catch((err: unknown) => {
        console.error("Error clearing custom theme:", err);
      });
    }
  };

  const setBackgroundStyle = (style: BackgroundStyle) => {
    setBackgroundStyleState(style);
    localStorage.setItem("fieldcheck-bg-style", style);
  };

  const toggleBackgroundStyle = () => {
    setBackgroundStyle(backgroundStyleState() === "gradients" ? "lines" : "gradients");
  };

  return (
    <ThemeContext.Provider
      value={{
        tokens,
        isDark,
        isBlur,
        followDevice,
        colorMode,
        selectColorMode,
        setColorMode,
        resetToDefaultTheme,
        toggleBlur,
        toggleFollowDevice,
        allThemes,
        systemIsDark,
        isThemeLoading: () => isPreferencesLoading() || themeQuery.isLoading,
        backgroundStyle: backgroundStyleState,
        setBackgroundStyle,
        toggleBackgroundStyle,
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
